import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import SolidPlusIcon from 'LuxKit/Icons/solid/SolidPlusIcon'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import NestableClickable from 'components/Common/NestableClickable'

export type PromptOption = {
  key: string
  name: string
  promptId?: string
  isDefault?: boolean
  notes?: string
}

interface PromptSelectorProps {
  prompts: Array<PromptOption>
  selectedPromptKey: string
  onSelect: (promptKey: string) => void
  onAddPrompt: () => void
  onEditPrompt?: (promptKey: string) => void
}

const OPENAI_PROMPT_DASHBOARD_URL = 'https://platform.openai.com/chat/edit'

function PromptSelector({
  prompts,
  selectedPromptKey,
  onSelect,
  onAddPrompt,
  onEditPrompt,
}: PromptSelectorProps) {
  const intl = useIntl()

  const openPromptAriaLabel = (promptName: string) => intl.formatMessage(
    {
      id: 'Wbr2vag1c7ZgA6bCxxoX',
      defaultMessage: 'Open {promptName} prompt in OpenAI dashboard',
      description: 'Aria label for the button to open a specific prompt in the OpenAI dashboard',
    },
    { promptName },
  )

  const editPromptAriaLabel = (promptName: string) => intl.formatMessage(
    {
      id: '_rVDLqpegakGITb5s-9b',
      defaultMessage: 'Edit {promptName} prompt',
      description: 'Aria label for the button to edit a specific prompt in the prompt selector',
    },
    { promptName },
  )

  const addPromptAriaLabel = intl.formatMessage({
    id: '3frpfVTMRSfOKNObDf-N',
    defaultMessage: 'Add prompt',
    description: 'Aria label for the button to add a new prompt in the TripPlanner prompt selector',
  })

  return (
    <Container>
      {prompts.map((prompt) => {
        const isSelected = prompt.key === selectedPromptKey
        const isEditable = !!onEditPrompt && !prompt.isDefault

        return (
          <PromptButton
            key={prompt.key}
            role="button"
            aria-pressed={isSelected}
            $isSelected={isSelected}
            onClick={() => onSelect(prompt.key)}
          >
            <PromptName>{prompt.name}</PromptName>
            {prompt.isDefault && prompt.promptId && (
              <InlineButton
                as="a"
                href={`${OPENAI_PROMPT_DASHBOARD_URL}?prompt=${prompt.promptId}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={openPromptAriaLabel(prompt.name)}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation()
                }}
              >
                <LineExternalLinkAltIcon size="S" />
              </InlineButton>
            )}
            {isEditable && (
              <InlineButton
                type="button"
                aria-label={editPromptAriaLabel(prompt.name)}
                onClick={(event) => {
                  event.stopPropagation()
                  onEditPrompt(prompt.key)
                }}
              >
                <LineEditAltIcon size="S" />
              </InlineButton>
            )}
          </PromptButton>
        )
      })}
      <AddPromptButton type="button" onClick={onAddPrompt} aria-label={addPromptAriaLabel}>
        <SolidPlusIcon size="S" />
      </AddPromptButton>
    </Container>
  )
}

export default PromptSelector

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const PromptButton = styled(NestableClickable)<{ $isSelected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  padding: ${rem(8)} ${rem(12)};
  border-radius: ${rem(12)};
  border: 1px solid
    ${props => (props.$isSelected ? props.theme.palette.neutral.default.four : props.theme.palette.neutral.default.six)};
  background: ${props => (props.$isSelected ? props.theme.palette.neutral.default.six : 'transparent')};
  color: ${props => props.theme.palette.neutral.default.one};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.1s ease;

  &:hover {
    border-color: ${props => props.theme.palette.neutral.default.five};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.palette.highlight.secondary.normalForeground};
    outline-offset: ${rem(2)};
  }
`

const PromptName = styled.span`
  white-space: nowrap;
`

const InlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(24)};
  height: ${rem(24)};
  margin-left: ${rem(4)};
  border-radius: ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.default.four};
  background: ${props => props.theme.palette.neutral.default.six};
  padding: ${rem(2)};
  color: ${props => props.theme.palette.neutral.default.one};
  cursor: pointer;

  &:hover {
    border-color: ${props => props.theme.palette.neutral.default.three};
    background: ${props => props.theme.palette.neutral.default.five};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.palette.highlight.secondary.normalForeground};
    outline-offset: ${rem(2)};
  }
`

const AddPromptButton = styled(PromptButton).attrs({
  $isSelected: false as const,
  role: 'button',
  'aria-pressed': false,
})<{ $isSelected?: boolean }>`
  padding: ${rem(8)};
  width: ${rem(36)};
  height: ${rem(36)};
  justify-content: center;
`
